import { type FormEvent, useState } from 'react'

import { TrashIcon } from 'lucide-react'

import { Button } from 'shared/components/ui/button'
import { Input } from 'shared/components/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from 'shared/components/ui/select'
import {
	DEFAULT_API_ENDPOINT,
	DEV_STORAGE_DEFAULTS,
	useStorage,
} from 'shared/lib/storage'
import { cn } from 'shared/lib/utils'

export const ApiEndpointSelect = () => {
	const [devStorage, updateDevStorage] = useStorage('dev', DEV_STORAGE_DEFAULTS)
	const { apiEndpoints, selectedApiEndpoint } = devStorage

	const [inputValue, setInputValue] = useState<string>(selectedApiEndpoint)
	const [urlError, setUrlError] = useState<string | null>(null)

	const validateUrl = (url: string) => {
		setUrlError(null)
		if (!url.trim()) {
			setUrlError('Endpoint cannot be empty.')
			return false
		}

		try {
			new URL(url)
		} catch {
			setUrlError('Invalid URL format.')
			return false
		}

		return true
	}

	const handleEndpointSelect = (value: string) => {
		updateDevStorage({ ...devStorage, selectedApiEndpoint: value })
		setInputValue(value)
	}

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault()
		if (!validateUrl(inputValue)) return
		updateDevStorage({ ...devStorage, selectedApiEndpoint: inputValue })
	}

	const handleAddEndpoint = () => {
		if (!validateUrl(inputValue)) return
		if (!isNewEndpoint) {
			setUrlError('Endpoint already exists.')
			return
		}

		updateDevStorage({
			...devStorage,
			apiEndpoints: [...apiEndpoints, inputValue],
			selectedApiEndpoint: inputValue,
		})
	}

	const handleDeleteEndpoint = (endpoint: string) => {
		const newList = apiEndpoints.filter((url) => url !== endpoint)
		const newSelected =
			selectedApiEndpoint === endpoint
				? DEFAULT_API_ENDPOINT
				: selectedApiEndpoint

		updateDevStorage({
			...devStorage,
			apiEndpoints: newList,
			selectedApiEndpoint: newSelected,
		})

		if (selectedApiEndpoint === endpoint) {
			setInputValue(DEFAULT_API_ENDPOINT)
		}
	}

	const isEmptyInput = !inputValue.trim()
	const isNewEndpoint = !apiEndpoints.includes(inputValue)

	return (
		<div className='space-y-4 p-4'>
			<h2 className='text-xl font-semibold'>API Endpoint Select</h2>

			<div className='space-y-2'>
				<label className='text-sm font-medium'>Select API Endpoint</label>
				<Select
					value={selectedApiEndpoint}
					onValueChange={handleEndpointSelect}
				>
					<SelectTrigger className='w-full'>
						<SelectValue placeholder='Select an endpoint' />
					</SelectTrigger>
					<SelectContent>
						{apiEndpoints.map((endpoint) => (
							<div className='group relative' key={endpoint}>
								<SelectItem value={endpoint}>{endpoint}</SelectItem>

								<div
									className={cn(
										'absolute right-1 top-1/2 -translate-y-1/2 hidden group-hover:block bg-primary-foreground',
										endpoint === DEFAULT_API_ENDPOINT && 'invisible',
									)}
								>
									<button
										className={cn(
											'flex items-center justify-center rounded-sm bg-red-500/10 text-red-500 hover:bg-red-500/20 py-1 px-0.5',
										)}
										onClick={() => handleDeleteEndpoint(endpoint)}
									>
										<TrashIcon className='size-4' />
									</button>
								</div>
							</div>
						))}
					</SelectContent>
				</Select>
			</div>

			<form className='flex flex-col gap-4' onSubmit={handleSubmit}>
				<div className='space-y-2'>
					<label className='text-sm font-medium'>Edit/Enter API Endpoint</label>
					<Input
						value={inputValue}
						onChange={(e) => setInputValue(e.target.value)}
						placeholder='Enter API endpoint (e.g., https://api.example.com)'
					/>
					{urlError && <p className='text-red-500 text-sm'>{urlError}</p>}
				</div>

				<div className='flex gap-2'>
					<Button disabled={isEmptyInput}>Submit</Button>

					<Button
						type='button'
						disabled={!isNewEndpoint || isEmptyInput}
						onClick={handleAddEndpoint}
					>
						Add New Endpoint
					</Button>
				</div>
			</form>

			<div className='mt-4 p-3 bg-gray-100 rounded-md'>
				<div className='text-sm font-medium mb-1'>Current Selection:</div>
				<code className='text-sm break-all'>{selectedApiEndpoint}</code>
			</div>
		</div>
	)
}
